
import { useMemo } from 'react';
import { useRouter } from '@/lib/navigation';
import { PremiumCategoryCard } from '@/components/help-center/PremiumCategoryCard';
import { useCms } from '@/components/painel/cms/cms-context';
import {
  HOME_PAGE_CATEGORY_COLOR_CLASSES,
  mergeCategoriesWithHomeDefaults,
} from '@/lib/help-center-home-categories';
import { getGuideCategoryHref } from '@/lib/guide-routes';

interface CategoriesProps {
  selectedCategory?: string | null;
  onSelectCategory?: (category: string, articleId?: number) => void;
  initialArticleId?: number | null;
  onBack?: () => void;
}

export default function Categories({
  selectedCategory,
  onSelectCategory,
  onBack,
}: CategoriesProps) {
  const router = useRouter();
  const { categories, articles } = useCms();

  const displayCategories = useMemo(
    () => mergeCategoriesWithHomeDefaults(categories),
    [categories]
  );

  const articleCountByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of articles) {
      m.set(a.categoryId, (m.get(a.categoryId) ?? 0) + 1);
    }
    return m;
  }, [articles]);

  const activeCategory = selectedCategory !== undefined ? selectedCategory : null;

  const handleSelectCategory = (id: string) => {
    if (onSelectCategory) {
      onSelectCategory(id);
      return;
    }
    router.push(getGuideCategoryHref(id));
  };

  if (activeCategory) {
    return null;
  }

  return (
    <section className="relative z-0 bg-gradient-to-b from-gray-50 to-white px-6 py-20">
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 text-center">
          <h2 className="mb-4 font-poppins text-4xl font-bold text-gray-900 md:text-5xl">
            Navegue por categoria
          </h2>
          <p className="mx-auto max-w-2xl font-poppins text-lg text-gray-600">
            Encontre respostas organizadas por tópico. Selecione uma categoria para explorar guias
            detalhados.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {displayCategories.map((category) => {
            const colorKey =
              category.color && HOME_PAGE_CATEGORY_COLOR_CLASSES[category.color]
                ? category.color
                : 'indigo';
            const colors = HOME_PAGE_CATEGORY_COLOR_CLASSES[colorKey];
            const badgeText =
              category.badge != null && String(category.badge).trim() !== ''
                ? String(category.badge).trim()
                : null;
            const count = articleCountByCategory.get(category.id) ?? 0;

            return (
              <PremiumCategoryCard
                key={category.id}
                category={{ ...category, badge: badgeText }}
                articleCount={count}
                iconClassName={colors.icon}
                badgeClassName={colors.badgeDark ?? 'bg-sky-500/20 text-sky-200'}
                badgeBorderColor={colors.badgeBorder ?? '#38bdf8'}
                hoverBorderClassName={colors.hoverBorder}
                hoverShadowClassName={colors.hoverShadow}
                onClick={() => handleSelectCategory(category.id)}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
